import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, Search } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";

const STATUS_STYLES = {
    Pending: "border-amber-500/40 bg-amber-500/10 text-amber-400",
    Dispatched: "border-cyan-500/40 bg-cyan-500/10 text-cyan-400",
    "In-Progress": "border-blue-500/40 bg-blue-500/10 text-blue-400",
    Completed: "border-emerald-500/40 bg-emerald-500/10 text-emerald-400",
    Cancelled: "border-red-500/40 bg-red-500/10 text-red-400",
};

export default function Track() {
    const [searchParams] = useSearchParams();
    const [ref, setRef] = useState(searchParams.get("ref") || "");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");

    const lookup = async (reference) => {
        if (!reference.trim()) return;
        setLoading(true);
        setError("");
        setResult(null);
        try {
            const { data } = await api.get(`/track/${encodeURIComponent(reference.trim())}`);
            setResult(data);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const initial = searchParams.get("ref");
        if (initial) lookup(initial);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="min-h-screen bg-[#060709] text-white flex flex-col" data-testid="track-page">
            <header className="border-b border-white/10 backdrop-blur-2xl bg-[#0B0C0E]/85">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/" data-testid="track-brand-link" className="flex items-center gap-3">
                        <Logo />
                        <span className="font-display font-extrabold uppercase">Masons Automotive</span>
                    </Link>
                    <Link
                        to="/"
                        data-testid="track-back-link"
                        className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-zinc-400 hover:text-amber-400 transition-colors duration-200"
                    >
                        <ArrowLeft className="w-4 h-4" /> Back to site
                    </Link>
                </div>
            </header>

            <main className="flex-1 flex items-center justify-center px-6 py-20">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                    className="w-full max-w-lg"
                >
                    <div className="text-xs font-mono uppercase tracking-[0.25em] text-amber-400 font-semibold mb-4">
                        Booking Tracker
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight leading-tight">
                        Where's my mechanic?
                    </h1>
                    <p className="mt-4 text-sm text-zinc-400 leading-relaxed">
                        Enter your booking reference (it looks like MSN-1234) from your confirmation email.
                    </p>

                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            lookup(ref);
                        }}
                        className="mt-8 flex gap-3"
                    >
                        <input
                            value={ref}
                            onChange={(e) => setRef(e.target.value.toUpperCase())}
                            placeholder="MSN-1234"
                            data-testid="track-ref-input"
                            className="flex-1 bg-[#0B0C0E] border border-[#383E4C] rounded-xl px-5 py-4 text-white font-mono text-sm placeholder:text-zinc-600 focus:outline-none focus:border-amber-500 transition-colors duration-200"
                        />
                        <motion.button
                            type="submit"
                            disabled={loading || !ref.trim()}
                            data-testid="track-submit-button"
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.97 }}
                            className="bg-amber-500 disabled:opacity-40 text-[#0B0C0E] font-bold uppercase tracking-widest text-xs rounded-xl px-6 py-4 hover:bg-amber-400 transition-colors duration-200 inline-flex items-center gap-2"
                        >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                            Track
                        </motion.button>
                    </form>

                    {error && (
                        <div className="mt-6 text-sm text-red-400 font-mono" data-testid="track-error">
                            {error}
                        </div>
                    )}

                    {result && (
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                            className="mt-8 rounded-2xl border border-[#22262F] bg-[#121418] p-8"
                            data-testid="track-result"
                        >
                            <div className="flex items-center justify-between gap-4 mb-6">
                                <span className="font-mono text-sm text-amber-400">{result.id}</span>
                                <span
                                    className={`text-[10px] font-mono uppercase tracking-[0.15em] border rounded-full px-3 py-1 ${STATUS_STYLES[result.status] || ""}`}
                                    data-testid="track-status"
                                >
                                    {result.status}
                                </span>
                            </div>
                            <div className="space-y-4">
                                <div>
                                    <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">Service</div>
                                    <div className="text-sm text-zinc-200 mt-1">{result.service}</div>
                                </div>
                                <div>
                                    <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">Vehicle</div>
                                    <div className="text-sm text-zinc-200 mt-1">{result.vehicle}</div>
                                </div>
                                <div>
                                    <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">Scheduled</div>
                                    <div className="text-sm text-zinc-200 mt-1">
                                        {result.date} · {result.time_slot}
                                    </div>
                                </div>
                            </div>
                            <p className="mt-6 pt-6 border-t border-[#22262F] text-xs text-zinc-500">
                                Questions about this booking? Call{" "}
                                <a href="tel:+61407663571" className="text-amber-400 hover:text-amber-300">
                                    +61 407 663 571
                                </a>
                            </p>
                        </motion.div>
                    )}
                </motion.div>
            </main>
        </div>
    );
}
